import { DISPLAY_DECIMALS, MON } from './format';

export interface MoneyValue {
  amount: number;
  currency: string;
}

const NO_VALUE = '—';
const RATE_SMALL_DECIMALS = 4;
const INR = 'INR';
const INR_LOCALE = 'en-IN';
const WESTERN_LOCALE = 'en-US';

// Distinct per currency so a "$" never stands for two currencies on one screen.
const SYMBOLS: Readonly<Record<string, string>> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
  AED: 'AED ',
  AUD: 'A$',
  CAD: 'CA$',
  SGD: 'S$',
};

/** The prefix shown before an amount: "₹", "$", "AED" (the code itself for one outside the 8). */
export function currencySymbol(currency: string | null | undefined): string {
  const code = (currency || INR).toUpperCase();
  return (SYMBOLS[code] ?? code).trim();
}

const formatters = new Map<string, Intl.NumberFormat>();

function digitsFormatter(currency: string): Intl.NumberFormat {
  const locale = currency === INR ? INR_LOCALE : WESTERN_LOCALE;
  let f = formatters.get(locale);
  if (!f) {
    f = new Intl.NumberFormat(locale, { minimumFractionDigits: DISPLAY_DECIMALS, maximumFractionDigits: DISPLAY_DECIMALS });
    formatters.set(locale, f);
  }
  return f;
}

/** "₹1,42,440.00" for INR (lakh/crore grouping), "$1,500.00" for the rest. Always 2 decimals, never abbreviated. A currency outside the 8 shows its code: "JPY 1,500.00". */
export function formatMoney(money: MoneyValue | null | undefined): string {
  if (!money || !Number.isFinite(money.amount)) return NO_VALUE;
  const code = (money.currency || INR).toUpperCase();
  const sign = money.amount < 0 ? '-' : '';
  const digits = digitsFormatter(code).format(Math.abs(money.amount));
  return `${sign}${SYMBOLS[code] ?? `${code} `}${digits}`;
}

const COMPACT_MIN = 100_000;
const INR_UNITS = [
  { size: 10_000_000, suffix: 'Cr' },
  { size: 100_000, suffix: 'L' },
] as const;
const WESTERN_UNITS = [
  { size: 1_000_000_000, suffix: 'B' },
  { size: 1_000_000, suffix: 'M' },
  { size: 1_000, suffix: 'K' },
] as const;

const compactDigits = new Map<string, Intl.NumberFormat>();

function compactFormatter(locale: string): Intl.NumberFormat {
  let f = compactDigits.get(locale);
  if (!f) {
    f = new Intl.NumberFormat(locale, { maximumFractionDigits: DISPLAY_DECIMALS });
    compactDigits.set(locale, f);
  }
  return f;
}

let roundingFormatter: Intl.NumberFormat | undefined;

// Built on first use: format.ts and this file import each other, so DISPLAY_DECIMALS is not set at module load.
function round2(n: number): number {
  roundingFormatter ??= new Intl.NumberFormat(WESTERN_LOCALE, { maximumFractionDigits: DISPLAY_DECIMALS, useGrouping: false });
  return Number(roundingFormatter.format(n));
}

/** Summary-surface money: "₹2.5L", "₹1.25Cr", "$250K", "$1.23M", "AED 250K". Units follow the currency (L/Cr for INR only), never the viewer. Below 100,000 (after rounding) it is the exact form. */
export function formatMoneyCompact(money: MoneyValue | null | undefined): string {
  if (!money || !Number.isFinite(money.amount)) return NO_VALUE;
  const abs = Math.abs(money.amount);
  const rounded = round2(abs);
  if (rounded < COMPACT_MIN) return formatMoney(money);
  const code = (money.currency || INR).toUpperCase();
  const isInr = code === INR;
  const units: ReadonlyArray<{ size: number; suffix: string }> = isInr ? INR_UNITS : WESTERN_UNITS;
  const index = units.findIndex((u) => rounded >= u.size);
  let unit = units[index] as { size: number; suffix: string };
  let scaled = round2(abs / unit.size);
  const larger = units[index - 1];
  if (larger && scaled * unit.size >= larger.size) {
    unit = larger;
    scaled = round2(abs / unit.size);
  }
  const sign = money.amount < 0 ? '-' : '';
  const digits = compactFormatter(isInr ? INR_LOCALE : WESTERN_LOCALE).format(scaled);
  return `${sign}${SYMBOLS[code] ?? `${code} `}${digits}${unit.suffix}`;
}

/** The note shown instead of a total while selected foreign lines have no rate yet. */
export function formatIncompleteNote(lines: number): string {
  if (lines <= 0) return 'Total incomplete: some lines without a rate';
  return `Total incomplete: ${lines} line${lines === 1 ? '' : 's'} without a rate`;
}

/** The converted figure shown beside an original: "≈ ₹1,42,440.00". */
export function formatApprox(converted: MoneyValue | null | undefined): string {
  return converted && Number.isFinite(converted.amount) ? `≈ ${formatMoney(converted)}` : '';
}

/** "2 Nov" from an ISO date ("2026-11-02"); empty when it isn't one. */
export function formatRateDate(isoDate: string): string {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(isoDate);
  const month = m ? MON[Number(m[1]) - 1] : undefined;
  return m && month ? `${Number(m[2])} ${month}` : '';
}

/** "at 2 Nov rate (1 USD = 95.08 INR)": the footnote for a converted figure. */
export function formatRateFootnote(from: string, to: string, rate: number, rateDate: string): string {
  const day = formatRateDate(rateDate);
  const digits = rate > 0 && rate < 1 ? RATE_SMALL_DECIMALS : DISPLAY_DECIMALS;
  const pair = `1 ${from.toUpperCase()} = ${rate.toFixed(digits)} ${to.toUpperCase()}`;
  return day ? `at ${day} rate (${pair})` : `(${pair})`;
}

/** The original, then ≈ the converted when the currencies differ; just the original otherwise (an INR line never shows "≈ ₹"). */
export function formatWithConverted(original: MoneyValue, converted: MoneyValue | null | undefined): string {
  const base = formatMoney(original);
  if (!converted || converted.currency.toUpperCase() === original.currency.toUpperCase()) return base;
  return `${base} ${formatApprox(converted)}`;
}

/** One line's price: the original, then "≈ <INR> at 2 Nov rate (1 USD = 95.08 INR)" from Go's `converted`. No converted (INR, or no fresh rate) shows the original only. */
export function formatLineMoney(
  original: MoneyValue,
  converted: (MoneyValue & { rate: number; rate_date: string }) | null | undefined,
): string {
  const base = formatWithConverted(original, converted);
  if (!converted || converted.currency.toUpperCase() === original.currency.toUpperCase()) return base;
  return `${base} ${formatRateFootnote(original.currency, converted.currency, converted.rate, converted.rate_date)}`;
}

export interface TotalsInfo {
  currency: string;
  rate_date?: string;
  converted: boolean;
}

/** A Go-computed total: "—" when Go sent null (a selected line has no fresh rate), else the amount. Never computed here. */
export function formatGoTotal(amount: number | null | undefined, info: Pick<TotalsInfo, 'currency'>): string {
  return amount == null ? NO_VALUE : formatMoney({ amount, currency: info.currency });
}

/** "in INR at today's rate (2 Nov)" when a non-INR line is inside the total, else empty. The rate date stays: a rate can be a day or more old. */
export function formatTotalNote(info: TotalsInfo | null | undefined): string {
  if (!info?.converted) return '';
  const code = info.currency.toUpperCase();
  const day = info.rate_date ? formatRateDate(info.rate_date) : '';
  return day ? `in ${code} at today's rate (${day})` : `in ${code} at today's rate`;
}
